import type { JSX } from 'react';
import { AppGlyph, Icon, appTileStyle } from '@ndrc/ui';
import type { AppIdentity } from '@ndrc/ui';
import { useActivityBadge } from '../activity/useActivityBadge';
import type { DeskManifest } from '../host/registry';
import { t } from '../messages';
import { useDesk } from './context';

/**
 * Le dock **est** le lanceur : il remplace la barre de tâches, comme dans la maquette.
 *
 * Il rend toutes les apps installées, activées ou non. Une app que l'enseignant n'a pas encore
 * ouverte pour la classe porte son **badge de cadenas** et ne se lance pas : c'est la pédagogie du
 * déblocage progressif rendue visible (§6 du cahier des charges). Ce n'est pas un bouton mort —
 * il dit ce qu'il est (`aria-disabled`, cadenas, info-bulle qui nomme la raison).
 *
 * **Il dit l'état réel de la pile** (7 septembre 2026) : un point d'exécution sous *chaque* app
 * ouverte, pas seulement sous la dernière. Recliquer une app déjà lancée la ramène au premier plan
 * — et la fait revenir si elle était réduite — au lieu d'en ouvrir un doublon. Ce n'est pas gardé
 * ici : `openApp` passe par la pile (`desk/stack.ts`), où une app ouverte est déjà une identité
 * connue.
 *
 * **Aucune app n'est nommée ici.** Nom, couleur et dessin viennent du manifeste, l'activation
 * vient de `desk.apps`. C'est ce qui permet à une app installée demain d'apparaître dans le dock
 * avec sa propre identité visuelle sans qu'une ligne de ce fichier ne change (§52 du cahier des
 * charges).
 */
export interface DockProps {
  apps: readonly DeskManifest[];
  /** Ids réellement activés pour la classe (résultat de `desk.apps`). */
  enabledAppIds: ReadonlySet<string>;
  /** Une absence de réponse ne prouve pas une désactivation. */
  activationState: 'loading' | 'unavailable' | 'ready';
  /** Les apps lancées, réduites ou non — elles portent le point d'exécution. */
  runningAppIds: ReadonlySet<string>;
  onOpenApp: (appId: string) => void;
  onOpenProfile: () => void;
  profileRunning: boolean;
  /** L'icône de l'espace enseignant, à droite du séparateur, à côté de « Mon compte ». **Ce n'est
   * pas une app** : elle ne vient pas du registre, elle ne s'active pas par classe, et un élève ne
   * la voit pas. Ne pas la montrer à un élève est une politesse, pas une protection — le serveur
   * refuse chaque opération de cet espace à un compte élève (voir `teacher/TeacherSpace.tsx`). */
  showTeacher: boolean;
  onOpenTeacher: () => void;
  teacherRunning: boolean;
  /** « Activité du jour » : la consigne de la séance, dans une fenêtre. **Ce n'est pas une app**
   * (elle ne vient pas du registre et ne s'active pas par classe), mais tout le monde la voit —
   * élève comme enseignant, ce dernier ayant besoin de lire ce que sa classe lit. Elle est là
   * parce qu'une consigne posée seulement sur le bureau nu disparaîtrait à la seconde où l'élève
   * ouvre son outil pour travailler, emportant avec elle « Vérifier » et « J'ai terminé ». */
  onOpenActivity: () => void;
  activityRunning: boolean;
  /** « Documents » : ce que l'enseignant a fait passer à la classe — l'entrée promise à l'élève par
   * le §5 du cahier des charges, et qui n'existait pas avant le 8 septembre 2026. **Ce n'est pas
   * une app** (elle ne vient pas du registre, elle ne s'active pas par classe), et tout le monde la
   * voit : l'enseignant a besoin de lire ce que sa classe a sous les yeux. Elle est à côté de
   * « Activité du jour » parce que c'est le même geste de séance — la consigne, et ce qui
   * l'accompagne. */
  onOpenDocuments: () => void;
  documentsRunning: boolean;
  onOpenPractice: () => void;
  practiceRunning: boolean;
  onOpenEnvironments: () => void;
  environmentsRunning: boolean;
}

/** Les deux tuiles du cœur (« Mon compte », « Espace enseignant ») sont peintes comme celles des
 * apps, avec la même fonction : le dock doit avoir l'air d'une seule matière. Elles ne dessinent
 * pas de marque à elles — ce sont des écrans du cœur, pas des produits — donc elles s'en tiennent
 * à une forme générique de la trousse. */
const PRACTICE_TILE: AppIdentity = { icon: 'clipboard', color: '#4338CA' };
const ENVIRONMENTS_TILE: AppIdentity = { icon: 'window', color: '#0F766E' };
const PROFILE_TILE: AppIdentity = { icon: 'user' };
const TEACHER_TILE: AppIdentity = { icon: 'clipboard', color: '#4338CA' };
/** `--teal-i` de la palette verrouillée (`docs/design/desktop-reference.md`) : distincte de
 * l'indigo de l'espace enseignant et de l'orange du fichier client, jamais une couleur inventée. */
const ACTIVITY_TILE: AppIdentity = { icon: 'target', color: '#0F766E' };
/** `--amber-i` de la palette verrouillée (`docs/design/desktop-reference.md`) : distincte du sarcelle
 * de l'activité, de l'indigo de l'espace enseignant et du gris de « Mon compte ». Jamais une couleur
 * inventée. */
const DOCUMENTS_TILE: AppIdentity = { icon: 'folder', color: '#B45309' };

export function Dock({
  apps,
  enabledAppIds,
  activationState,
  runningAppIds,
  onOpenApp,
  onOpenProfile,
  profileRunning,
  showTeacher,
  onOpenTeacher,
  teacherRunning,
  onOpenActivity,
  activityRunning,
  onOpenDocuments,
  documentsRunning,
  onOpenPractice,
  practiceRunning,
  onOpenEnvironments,
  environmentsRunning,
}: DockProps): JSX.Element {
  return (
    <nav className="dock" aria-label={t('dock.label')}>
      {apps.map((app) => {
        const enabled = activationState === 'ready' && enabledAppIds.has(app.id);
        const locked = activationState === 'ready' && !enabled;
        const tip =
          activationState === 'loading'
            ? t('dock.loading', { name: app.name })
            : activationState === 'unavailable'
              ? t('dock.unavailable', { name: app.name })
              : enabled
                ? app.name
                : t('dock.locked', { name: app.name });
        return (
          <div className="dock-item" key={app.id}>
            <button
              type="button"
              className="dock-icon"
              style={appTileStyle(app)}
              aria-disabled={enabled ? undefined : true}
              aria-label={tip}
              onClick={() => {
                if (enabled) onOpenApp(app.id);
              }}
            >
              <AppGlyph app={app} size={23} />
              {locked ? (
                <span className="dock-lock">
                  <Icon name="lock" size={9} strokeWidth={2.2} />
                </span>
              ) : null}
              {runningAppIds.has(app.id) ? <span className="dock-dot" /> : null}
            </button>
            <span className="dock-tip">{tip}</span>
          </div>
        );
      })}

      {apps.length > 0 ? <span className="dock-sep" aria-hidden="true" /> : null}

      <div className="dock-item">
        <button
          type="button"
          className="dock-icon"
          style={appTileStyle(PRACTICE_TILE)}
          aria-label={t('practice.title')}
          onClick={onOpenPractice}
        >
          <AppGlyph app={PRACTICE_TILE} size={23} />
          {practiceRunning ? <span className="dock-dot" /> : null}
        </button>
        <span className="dock-tip">{t('practice.title')}</span>
      </div>
      <ActivityTile running={activityRunning} onOpen={onOpenActivity} />
      <div className="dock-item">
        <button
          type="button"
          className="dock-icon"
          style={appTileStyle(ENVIRONMENTS_TILE)}
          aria-label={t('environments.title')}
          onClick={onOpenEnvironments}
        >
          <AppGlyph app={ENVIRONMENTS_TILE} size={23} />
          {environmentsRunning ? <span className="dock-dot" /> : null}
        </button>
        <span className="dock-tip">{t('environments.title')}</span>
      </div>

      <div className="dock-item">
        <button
          type="button"
          className="dock-icon"
          style={appTileStyle(DOCUMENTS_TILE)}
          aria-label={t('dock.documents')}
          onClick={onOpenDocuments}
        >
          <AppGlyph app={DOCUMENTS_TILE} size={23} />
          {documentsRunning ? <span className="dock-dot" /> : null}
        </button>
        <span className="dock-tip">{t('dock.documents')}</span>
      </div>

      <div className="dock-item">
        <button
          type="button"
          className="dock-icon"
          style={appTileStyle(PROFILE_TILE)}
          aria-label={t('dock.profile')}
          onClick={onOpenProfile}
        >
          <AppGlyph app={PROFILE_TILE} size={23} />
          {profileRunning ? <span className="dock-dot" /> : null}
        </button>
        <span className="dock-tip">{t('dock.profile')}</span>
      </div>

      {showTeacher ? (
        <div className="dock-item">
          <button
            type="button"
            className="dock-icon"
            style={appTileStyle(TEACHER_TILE)}
            aria-label={t('dock.teacherSpace')}
            onClick={onOpenTeacher}
          >
            <AppGlyph app={TEACHER_TILE} size={23} />
            {teacherRunning ? <span className="dock-dot" /> : null}
          </button>
          <span className="dock-tip">{t('dock.teacherSpace')}</span>
        </div>
      ) : null}
    </nav>
  );
}

/**
 * La tuile « Activité du jour », et **sa pastille**.
 *
 * C'est la seule tuile du dock qui lit une donnée : les autres ne peignent que ce qu'on leur passe.
 * La raison est la moitié manquante de la promesse du 7 septembre 2026 — « Vos élèves verront la
 * consigne apparaître sur leur bureau sans avoir à recharger leur page » était vrai de la consigne
 * elle-même, et faux du bureau : **rien** n'apparaissait, ni pastille ni notification. Un élève
 * travaillant en plein écran dans son outil ne savait pas qu'une consigne venait d'être lancée.
 *
 * Extraite en composant plutôt qu'écrite dans `Dock` : un hook ne peut pas se poser au milieu d'un
 * `return`, et remonter cette lecture jusqu'à `Desk` pour la redescendre en propriété n'aurait fait
 * traverser un état d'affichage purement local à deux fichiers de plus. `useDesk()` donne ici ce
 * dont le badge a besoin (qui lit, et sa classe) sans élargir `DockProps` — voir
 * `activity/useActivityBadge.ts` pour la règle exacte de « vue ».
 *
 * `running` (l'app est ouverte) et la pastille sont **deux choses différentes**, et elles sont
 * dessinées à deux endroits différents : le point d'exécution sous la tuile dit « cette fenêtre est
 * ouverte », la pastille en haut à droite dit « quelque chose est arrivé que vous n'avez pas
 * encore lu ».
 */
function ActivityTile({ running, onOpen }: { running: boolean; onOpen: () => void }): JSX.Element {
  const { me, classId } = useDesk();
  const unseen = useActivityBadge({ userId: me.id, classId, opened: running });
  const label = unseen ? t('dock.activityNew') : t('dock.activity');
  return (
    <div className="dock-item">
      <button
        type="button"
        className="dock-icon"
        style={appTileStyle(ACTIVITY_TILE)}
        aria-label={label}
        onClick={onOpen}
      >
        <AppGlyph app={ACTIVITY_TILE} size={23} />
        {unseen ? <span className="dock-badge" /> : null}
        {running ? <span className="dock-dot" /> : null}
      </button>
      <span className="dock-tip">{label}</span>
    </div>
  );
}
